import styled, { keyframes, css } from "styled-components";

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40%           { transform: scale(1);   opacity: 1;   }
`;

// ─── Floating Button ─────────────────────────────────────────────────────────

export const FloatingButton = styled.button`
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ theme }) => theme.gradient_primary};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 24px ${({ theme }) => theme.shadow_primary},
              0 2px 8px ${({ theme }) => theme.shadow};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  z-index: 1000;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 32px ${({ theme }) => theme.primary_glow},
                0 2px 8px ${({ theme }) => theme.shadow};
  }

  svg {
    width: 24px;
    height: 24px;
    color: #fff;
  }

  @media (max-width: 480px) {
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
  }
`;

// ─── Chat Panel ───────────────────────────────────────────────────────────────

export const ChatPanel = styled.div`
  position: fixed;
  bottom: 100px;
  right: 32px;
  width: 380px;
  height: 540px;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.card_border};
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px ${({ theme }) => theme.shadow},
              0 4px 20px ${({ theme }) => theme.shadow_primary};
  z-index: 999;
  animation: ${fadeSlideUp} 0.25s ease;

  @media (max-width: 480px) {
    width: calc(100vw - 24px);
    right: 12px;
    bottom: 80px;
    /* Use dvh (dynamic viewport height) so the panel doesn't go behind
       the browser nav bar or phone home indicator on iOS/Android */
    height: calc(var(--vh, 1vh) * 72);
    max-height: 600px;
    border-radius: 16px;
  }
`;

// ─── Header ───────────────────────────────────────────────────────────────────

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.divider};
  background: ${({ theme }) => theme.card};
  flex-shrink: 0;
`;

export const HeaderAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${({ theme }) => theme.gradient_primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    color: #fff;
  }
`;

export const HeaderText = styled.div`
  flex: 1;

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_primary};
    font-family: "Space Grotesk", sans-serif;
  }

  p {
    margin: 0;
    font-size: 11px;
    color: ${({ theme }) => theme.text_secondary};
    font-family: "Space Grotesk", sans-serif;
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: ${({ theme }) => theme.text_primary};
    background: ${({ theme }) => theme.primary_light};
  }

  svg { width: 18px; height: 18px; }
`;

// ─── Messages Area ────────────────────────────────────────────────────────────

export const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.divider};
    border-radius: 4px;
  }
`;

// ─── Welcome / Starter prompts ────────────────────────────────────────────────

export const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0 4px;
  gap: 8px;
`;

export const WelcomeText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
  text-align: center;
  margin: 0;
  line-height: 1.5;
  font-family: "Space Grotesk", sans-serif;
`;

export const StarterPromptsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  width: 100%;
  margin-top: 4px;
`;

export const StarterPrompt = styled.button`
  background: ${({ theme }) => theme.primary_light};
  border: 1px solid ${({ theme }) => theme.card_border};
  border-radius: 10px;
  padding: 9px 13px;
  min-height: 44px; /* Apple/Google recommended minimum touch target */
  color: ${({ theme }) => theme.text_primary};
  font-size: 12.5px;
  font-family: "Space Grotesk", sans-serif;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s, transform 0.1s;
  line-height: 1.4;

  &:hover {
    background: ${({ theme }) => theme.primary_glow};
    border-color: ${({ theme }) => theme.primary};
    transform: translateX(3px);
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

// ─── Message Bubbles ──────────────────────────────────────────────────────────

export const MessageRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ $isUser }) => ($isUser ? "flex-end" : "flex-start")};
  gap: 4px;
`;

export const Bubble = styled.div`
  max-width: 88%;
  padding: 10px 14px;
  border-radius: ${({ $isUser }) =>
    $isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px"};
  font-size: 13.5px;
  line-height: 1.6;
  font-family: "Space Grotesk", sans-serif;
  word-break: break-word;
  white-space: pre-wrap;

  ${({ $isUser, theme }) =>
    $isUser
      ? css`
          background: ${theme.gradient_primary};
          color: #fff;
        `
      : css`
          background: ${theme.bgLight};
          color: ${theme.text_primary};
          border: 1px solid ${theme.divider};
        `}
`;

// Blinking cursor shown at the end of a streaming message
export const StreamCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 14px;
  background: ${({ theme }) => theme.primary};
  margin-left: 2px;
  vertical-align: middle;
  border-radius: 1px;
  animation: ${blink} 0.9s ease infinite;
`;

// ─── Typing Indicator ─────────────────────────────────────────────────────────

export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.bgLight};
  border: 1px solid ${({ theme }) => theme.divider};
  border-radius: 16px 16px 16px 4px;
  width: fit-content;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.primary};
    animation: ${dotPulse} 1.2s ease infinite;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

// ─── Input Area ───────────────────────────────────────────────────────────────

export const InputArea = styled.div`
  padding: 12px 14px;
  border-top: 1px solid ${({ theme }) => theme.divider};
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.card};
  flex-shrink: 0;
`;

export const TextInput = styled.input`
  flex: 1;
  background: ${({ theme }) => theme.bgLight};
  border: 1px solid ${({ theme }) => theme.divider};
  border-radius: 12px;
  padding: 10px 14px;
  font-size: 16px; /* 16px prevents iOS auto-zoom on focus */
  color: ${({ theme }) => theme.text_primary};
  font-family: "Space Grotesk", sans-serif;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder { color: ${({ theme }) => theme.text_tertiary}; }

  &:focus {
    border-color: ${({ theme }) => theme.primary};
  }
`;

export const SendButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${({ theme }) => theme.gradient_primary};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s, transform 0.1s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  &:not(:disabled):hover { transform: scale(1.06); }

  svg { width: 17px; height: 17px; color: #fff; }
`;
